import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { CUSTOM_DATE_SEARCH_DEBOUNCE_MS } from '../constants';
import { useSetSearchUnitHistoryResponse } from '../context/hooks';
import { isUnitHistoryFormValid } from '../is-unit-history-form-valid';
import { UnitHistoryForm } from '../types';
import { timeRangeValueToDateRange } from '../utils';

export function useRunUnitHistorySearchOnChange(): void {
    const setSearchUnitHistoryResponse = useSetSearchUnitHistoryResponse();
    const { form, mutation } = useIntegratedForm<UnitHistoryForm>();
    const { unitOption, userOption, dateRange, sortTypeOption } = form.watch();
    const mutate = mutation.mutate;

    const runSearch = useCallback(
        function runUnitHistorySearch() {
            const formValues = form.getValues();
            let dateRange = formValues.dateRange;

            if (formValues.timeRangeOption && formValues.timeRangeOption.value !== 'CUSTOM') {
                dateRange = timeRangeValueToDateRange(formValues.timeRangeOption.value);
            }

            if (isUnitHistoryFormValid(formValues)) {
                setSearchUnitHistoryResponse(undefined);
                mutate({ ...formValues, dateRange });
            }
        },
        [form, mutate, setSearchUnitHistoryResponse]
    );

    const debouncedRunSearch = useMemo(() => debounce(runSearch, CUSTOM_DATE_SEARCH_DEBOUNCE_MS), [
        runSearch,
    ]);

    const prevNonDateValues = useRef({ unitOption, userOption, sortTypeOption });

    useEffect(
        function runSearchOnFormChange() {
            const prev = prevNonDateValues.current;
            const nonDateValueChanged =
                prev.unitOption !== unitOption ||
                prev.userOption !== userOption ||
                prev.sortTypeOption !== sortTypeOption;
            prevNonDateValues.current = { unitOption, userOption, sortTypeOption };

            debouncedRunSearch.cancel();
            if (form.getValues().timeRangeOption?.value === 'CUSTOM' && !nonDateValueChanged) {
                debouncedRunSearch();
            } else {
                runSearch();
            }

            return () => debouncedRunSearch.cancel();
        },
        [form, runSearch, debouncedRunSearch, unitOption, userOption, dateRange, sortTypeOption]
    );
}
